import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Formik, Form, FormikValues } from 'formik';
import * as yup from 'yup';

import PhoneInput from 'components/PhoneInput';
import { CountryCode } from 'components/Selects/CountryCodeSelect';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { htmlT } from 'utils/generic';

import {
  Container,
  Header,
  Subheader,
  Detail,
  FieldsContainer,
  BackButton,
  NextButton,
  ButtonsContainer,
} from '../../styles';
import { CountryValues } from '../Country';
import { ErrorTag } from './styles';

export type OTPChannel = 'whatsapp' | 'sms';

export interface TelephoneValues {
  phone: string;
  country_code: CountryCode;
}

interface Props {
  values: TelephoneValues | null;
  onSubmit: (values: TelephoneValues, channel: OTPChannel) => Promise<any>;
  countryCodes: CountryCode[];
  country: CountryValues | null;
  variant?: 'incorrect-number' | 'register-number';
}

const Telephone = ({ values, onSubmit, countryCodes, country: countryValues, variant }: Props) => {
  const { t } = useStoreTranslation();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [otpChannel, setOtpChannel] = useState<OTPChannel>('whatsapp');

  const isDefaultVariant = variant === undefined;
  const isIncorrectNumberVariant = variant === 'incorrect-number';
  const isRegisterNumberVariant = variant === 'register-number';

  if (!countryValues || countryCodes.length === 0) return null;

  const validationSchema = yup.object().shape({
    phone: yup.string().phone().required(),
    country_code: yup.object().required(),
  });

  const initialValues: TelephoneValues = {
    phone: '',
    country_code: countryCodes.find((c) => c.id === countryValues?.country.id) as CountryCode,
  };

  const handleBack = () => history.goBack();

  const handleSubmit = async (formValues: TelephoneValues) => {
    setLoading(true);
    await onSubmit(formValues, otpChannel);
    setLoading(false);
  };

  const getHeaderContent = () => {
    if (isRegisterNumberVariant) return t('registrationOnboarding.telephone.registerNumber.title');
    return t('registrationOnboarding.telephone.title');
  };
  const getSubheaderContent = () => {
    if (isIncorrectNumberVariant) {
      if (values)
        return htmlT(t, 'registrationOnboarding.telephone.incorrectNumber.subtitle', {
          country_code: values.country_code.code,
          number: values.phone,
        });
      return t('registrationOnboarding.telephone.incorrectNumber.subtitleNoPhone');
    }
    if (isRegisterNumberVariant) return t('registrationOnboarding.telephone.registerNumber.subtitle');
    return t('registrationOnboarding.telephone.subtitle');
  };

  return (
    <Formik
      initialStatus={{ rightCheckIfIsValid: true }}
      initialValues={isDefaultVariant ? values || initialValues : initialValues}
      isInitialValid={(formik: any) => validationSchema.isValidSync(formik.initialValues)}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isValid, submitForm }: FormikValues) => (
        <Form>
          <Container>
            <Header>{getHeaderContent()}</Header>
            {isIncorrectNumberVariant && (
              <ErrorTag>{t('registrationOnboarding.telephone.incorrectNumber.tag')}</ErrorTag>
            )}
            <Subheader>{getSubheaderContent()}</Subheader>
            {isDefaultVariant && <Detail>{t('registrationOnboarding.telephone.detail')}</Detail>}

            <FieldsContainer>
              <PhoneInput
                phoneField="phone"
                countryCodeField="country_code"
                countryCodes={countryCodes}
                mask={'9'.repeat(50)}
              />
            </FieldsContainer>

            <ButtonsContainer>
              <BackButton type="button" onClick={handleBack}>
                {t('registrationOnboarding.backButton')}
              </BackButton>
              <NextButton
                type="button"
                disabled={!isValid || loading}
                loading={loading && otpChannel === 'sms'}
                onClick={() => {
                  setOtpChannel('sms');
                  submitForm();
                }}
              >
                {t('registrationOnboarding.telephone.sendCode.sms')}
              </NextButton>
              <NextButton
                type="button"
                disabled={!isValid || loading}
                loading={loading && otpChannel === 'whatsapp'}
                onClick={() => {
                  setOtpChannel('whatsapp');
                  submitForm();
                }}
              >
                {t('registrationOnboarding.telephone.sendCode.whatsapp')}
              </NextButton>
            </ButtonsContainer>
          </Container>
        </Form>
      )}
    </Formik>
  );
};

export default Telephone;
